import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import toast from "react-hot-toast";
import { workspaceService } from "../../services/api";
export default function AssistantPreferences() {
  const client = useQueryClient();
  const query = useQuery({
    queryKey: ["workspace-preferences"],
    queryFn: workspaceService.preferences,
  });
  const [values, setValues] = useState(null);
  const [busy, setBusy] = useState(false);
  const [dirty, setDirty] = useState(false);
  useEffect(() => {
    if (query.data && !dirty) setValues(query.data);
  }, [query.data, dirty]);
  const change = (key, value) => {
    setDirty(true);
    setValues((old) => ({ ...old, [key]: value }));
  };
  const save = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      const saved = await workspaceService.savePreferences({
        ...values,
        onboarded: true,
      });
      client.setQueryData(["workspace-preferences"], saved);
      setValues(saved);
      setDirty(false);
      toast.success("Your assistant preferences are saved.");
    } catch {
      toast.error("Could not save. Your edits are still here.");
    } finally {
      setBusy(false);
    }
  };
  if (query.isPending)
    return (
      <main className="unified-page" role="status">
        Loading your preferences…
      </main>
    );
  if (query.error)
    return (
      <main className="unified-page">
        <p role="alert">Settings couldn’t load.</p>
        <button onClick={() => query.refetch()}>Try again</button>
      </main>
    );
  return (
    <main className="unified-page">
      <header className="unified-page-header">
        <div>
          <p className="workspace-eyebrow">Your assistant</p>
          <h1>Make it yours.</h1>
          <p>
            Keep the context that matters visible, editable and under your
            control.
          </p>
        </div>
      </header>
      {values && (
        <form className="assistant-preferences-form" onSubmit={save}>
          <label>
            Assistant name
            <input
              className="field-input"
              value={values.assistant_name}
              required
              maxLength={40}
              onChange={(e) => change("assistant_name", e.target.value)}
            />
          </label>
          <label>
            What should your assistant remember?
            <textarea
              className="field-input"
              rows={9}
              maxLength={5000}
              value={values.memory}
              placeholder="Your priorities, preferences and what a useful day looks like. You can change or remove this at any time."
              onChange={(e) => change("memory", e.target.value)}
            />
          </label>
          <p className="workspace-caption">
            This context is used when answering future requests. It does not
            authorize purchases or messages. Removing it here stops its use as
            saved memory; earlier conversations keep their original text.
          </p>
          <button className="primary-button" disabled={busy || !dirty}>
            {busy ? "Saving…" : "Save preferences"}
          </button>
        </form>
      )}
      <section className="settings-links">
        <Link to="/manage?view=settings">
          <strong>Notifications & action preferences</strong>
          <span>Quiet hours, delivery and review settings.</span>
        </Link>
        <Link to="/integrations">
          <strong>Connected apps</strong>
          <span>Choose which services provide context and actions.</span>
        </Link>
        <Link to="/manage?view=agent">
          <strong>Scheduled work</strong>
          <span>Inspect, pause or change recurring research.</span>
        </Link>
        <Link to="/profile">
          <strong>Account</strong>
          <span>Your timezone, account details and sign-out.</span>
        </Link>
        <Link to="/library">
          <strong>Pinned workspaces</strong>
          <span>Choose what appears in your sidebar.</span>
        </Link>
      </section>
    </main>
  );
}
